import { InboxNotiDto } from '@diritto/common/notification/dtos/inbox-noti.dto';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import { convertToDotString, convertToRelativeString } from '@diritto/common/utils/date';
import styled from '@emotion/styled';
import { ReplyApi } from 'api/ApiHelper';
import { ModalType } from 'constants/modal';
import { useAuthState } from 'contexts/AuthProvider';
import { ReReplyCircleIcon } from 'icons/ReReplyCircleIcon';
import * as Styled from 'app-components/inbox/items/styles';
import React, { useCallback, useState } from 'react';
import { getTextLineStyle } from 'styles/common';
import { Divider } from 'ui-components/common/Divider';
import { Link } from 'ui-components/common/Link';
import { MoreMenu } from 'ui-components/common/MoreMenu';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { showToast } from 'utils/modal';
import { ModalHandler } from 'utils/ModalHandler';

interface ReReplyInboxNotiListItemProps {
  inboxNoti: InboxNotiDto;
  link: string;
  replyId: number;
}

export const ReReplyInboxNotiListItem = ({ inboxNoti, link, replyId }: ReReplyInboxNotiListItemProps) => {
  const [isRead] = useState(inboxNoti.isRead);
  const ctxString = '답글';
  const { isLoggedIn } = useAuthState();
  const requestSign = useRequestSign();

  const handleReport = useCallback(async () => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }

    try {
      const data = await ReplyApi.isReported(replyId);
      if (data.isReported) {
        showToast(`이미 신고한 ${ctxString}이에요.`);
        return;
      }
    } catch {
      showToast('일시적인 오류로 신고하지 못했어요. 잠시 뒤 다시 시도해주세요.');
      return;
    }

    ModalHandler.show(ModalType.ReplyReport, { replyId, ctxString });
  }, [ctxString, isLoggedIn, replyId, requestSign]);

  return (
    <Link href={link}>
      <Styled.Container>
        <StyledWrapper>
          <Styled.LeftIconSection>
            <Styled.Unread isRead={isRead} />
            <ReReplyCircleIcon className="size-[56px]" />
          </Styled.LeftIconSection>
          <Styled.RightSection>
            <Styled.TitleWrapper>
              <Styled.SanitizeHTMLTitle
                html={`${inboxNoti.title}<span class="inbox-title-date" title="${convertToDotString(
                  inboxNoti.sentAt,
                )}">${convertToRelativeString(inboxNoti.sentAt)}</span>`}
                options={{
                  allowedTags: ['b', 'span', 'br'],
                  allowedClasses: {
                    span: ['inbox-title-date'],
                  },
                  allowedAttributes: { span: ['title'] },
                }}
              />
            </Styled.TitleWrapper>
            <Styled.Extra>
              <StyledReplyDesc>&quot;{inboxNoti?.description}&quot;</StyledReplyDesc>
            </Styled.Extra>
          </Styled.RightSection>
          <StyledButtonContainer>
            <MoreMenu vertical menu={[{ title: '신고', onClick: handleReport }]} />
          </StyledButtonContainer>
        </StyledWrapper>
      </Styled.Container>
      <Divider color={Color.Gray200} />
    </Link>
  );
};

const StyledWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  background: white;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: row;
`;

const StyledReplyDesc = styled.p`
  ${Typography.XXSmall}
  ${getTextLineStyle(3)};
  color: ${Color.Gray600};
`;
